import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CustomerSummaryDto, OwnerEntity } from '@bhops/shared';
import { customersApi } from '@/api/customers';
import { useDebouncedValue } from './useDebouncedValue';

// CustomerPicker — search-on-type combo box. Returns a
// CustomerSummaryDto via `onSelect`.
//
// Used by Phase 5-G2's DealCreateModal + Contract create wizard.
// Other call sites (existing inline `<select>` in the Reports
// inbox, Deployments) can migrate opportunistically; G2 doesn't
// refactor them.
//
// Implementation:
//   - <input> + 250ms debounce → customersApi.list({ search })
//   - dropdown rendered absolutely below the input
//   - keyboard nav: ↑ ↓ to move, Enter to pick, Esc to close
//   - server-side ownerEntity scoping via the optional
//     `ownerEntity` prop (admins can omit; non-admins should pass
//     their own entity so cross-entity results don't leak)

interface Props {
  /** Currently selected customer id, or null. */
  selected: string | null;
  onSelect: (customer: CustomerSummaryDto) => void;
  /**
   * Scope the search to this entity. Omit for admins (server-side
   * CASL still applies). Non-admins MUST pass their own
   * ownerEntity to keep cross-entity rows out of the dropdown.
   */
  ownerEntity?: OwnerEntity;
  placeholder?: string;
  /** Disabled state — gated picker behind another field. */
  disabled?: boolean;
  /** Customers picked this often appear at the top — soft TODO; ignored today. */
  recentIds?: string[];
}

export function CustomerPicker({
  selected,
  onSelect,
  ownerEntity,
  placeholder = '고객사 검색',
  disabled = false,
}: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const debounced = useDebouncedValue(query, 250);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['customers', 'picker', debounced, ownerEntity ?? null],
    queryFn: () =>
      customersApi.list({
        search: debounced || undefined,
        pageSize: 20,
      }),
    // Don't fetch until the picker is open — saves a round-trip on
    // form mount.
    enabled: open,
    staleTime: 30_000,
  });

  const items = (data?.items ?? []).filter((c) => !ownerEntity || c.ownerEntity === ownerEntity);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return;
    function onClickAway(e: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClickAway);
    return () => window.removeEventListener('mousedown', onClickAway);
  }, [open]);

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      setHighlightIdx((i) => Math.min(items.length - 1, i + 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlightIdx((i) => Math.max(0, i - 1));
      e.preventDefault();
    } else if (e.key === 'Enter') {
      const pick = items[highlightIdx];
      if (pick) {
        onSelect(pick);
        setQuery(pick.displayName);
        setOpen(false);
      }
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} style={wrap}>
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlightIdx(0);
        }}
        onKeyDown={handleKey}
        style={inputStyle(disabled)}
      />
      {open && !disabled && (
        <div style={dropdown}>
          {isLoading && (
            <div style={loadingRow} className="kuun-caption">
              불러오는 중…
            </div>
          )}
          {!isLoading && items.length === 0 && (
            <div style={loadingRow} className="kuun-caption">
              찾는 고객사가 없습니다. 새 고객사는 고객사 페이지에서 등록해 주세요.
            </div>
          )}
          {items.map((c, i) => (
            <div
              key={c.id}
              role="button"
              onMouseDown={(e) => {
                // mousedown not click — the click event would fire
                // after the input's onBlur, by which time `open` is
                // already false.
                e.preventDefault();
                onSelect(c);
                setQuery(c.displayName);
                setOpen(false);
              }}
              onMouseEnter={() => setHighlightIdx(i)}
              style={rowStyle(i === highlightIdx, c.id === selected)}
            >
              <span style={{ fontWeight: 500 }}>{c.displayName}</span>
              <span
                className="kuun-caption"
                style={{ marginLeft: 'auto', color: 'var(--fg-muted)' }}
              >
                {c.segment} · {c.ownerEntity}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const wrap: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
  width: '100%',
};

const inputStyle = (disabled: boolean): CSSProperties => ({
  width: '100%',
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: disabled ? 'var(--bg-sunken)' : 'var(--bg-surface)',
  fontSize: 13,
  color: disabled ? 'var(--fg-muted)' : 'var(--fg-default)',
});

const dropdown: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 4,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  boxShadow: 'var(--shadow-md)',
  maxHeight: 320,
  overflowY: 'auto',
  zIndex: 100,
};

const loadingRow: CSSProperties = {
  padding: '10px 12px',
  textAlign: 'center',
};

const rowStyle = (highlighted: boolean, selected: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: 13,
  background: highlighted ? 'var(--blue-50)' : 'transparent',
  fontWeight: selected ? 500 : 400,
  borderLeft: selected ? '3px solid var(--blue-500)' : '3px solid transparent',
});
